import type { LiabilityOffer, PricingResponse, SyncBackPet } from "@/shared/types/lead-api-types"
import { getLiabilityState } from "@/shared/utils/storage"
import { isDangerousBreed } from "./coverage"

/** Single fixed RC plan for the DE flow. */
export const RC_ANNUAL_LIMIT = 10_000_000
/** Cleos forbids a 0€ franchise on dangerous dogs, so they get 150€ while every other dog gets 0€. */
export const RC_FRANCHISE_DANGEROUS = 150
export const RC_FRANCHISE_STANDARD = 0

/** The franchise (deductible) shown/priced for a dog: 150€ for dangerous breeds, 0€ otherwise. */
export function getRcFranchise(pet: Partial<SyncBackPet> | undefined): 0 | 150 {
  const race = pet?.breed && "race" in pet.breed ? pet.breed.race : undefined
  return isDangerousBreed(race) ? RC_FRANCHISE_DANGEROUS : RC_FRANCHISE_STANDARD
}

/** The RC add-on is dog-only. A pet contributes its RC premium iff it's a dog with `liability` selected. */
export function isLiabilitySelected(pet: Partial<SyncBackPet>): boolean {
  return pet.type === "dog" && pet.liability === true
}

/** Monthly RC premium this specific pet adds to the cart (0 unless it's a selected dog). */
export function getPetLiabilityAddOn(pet: Partial<SyncBackPet>): number {
  return isLiabilitySelected(pet) ? pet.rc_price ?? 0 : 0
}

/** True when at least one dog has the RC add-on selected. */
export function isAnyLiabilitySelected(pets: Partial<SyncBackPet>[] | undefined): boolean {
  return (pets ?? []).some(isLiabilitySelected)
}

/**
 * Re-applies the per-dog RC selection (liability + price + claims) from
 * sessionStorage onto pets. The backend quote does not carry the RC add-on, so
 * `formatQuoteResponse` drops it — call this after a backend quote refresh
 * (retrieve / recap reload) to avoid losing the selection.
 */
export function applyStoredLiability(
  pets: Partial<SyncBackPet>[] | undefined
): Partial<SyncBackPet>[] {
  const stored = getLiabilityState()
  if (!stored) return pets ?? []
  return (pets ?? []).map((pet) => {
    // Backend is the source of truth now (GET /quote returns the liability leg);
    // only fall back to sessionStorage when the pet carries no backend RC data.
    if (pet.liability !== undefined) return pet
    const petState = pet.idx != null ? stored[pet.idx] : undefined
    if (!petState) return pet
    return {
      ...pet,
      liability: petState.selected,
      rc_price: petState.price,
      has_previous_insurance: petState.previouslyInsured ?? undefined,
      number_of_previous_claims:
        petState.claimsCount != null ? (Number(petState.claimsCount) as 0 | 1 | 2 | 3) : undefined,
    }
  })
}


export function getRcLiabilityOffers(
  prices: PricingResponse | undefined,
  petIdx: number
): LiabilityOffer[] {
  return prices?.[petIdx]?.offers?.liability ?? []
}

export function extractRcPrice(
  prices: PricingResponse | undefined,
  petIdx: number,
  _hasPreviousInsurance: boolean,
  numberOfPreviousClaims: 0 | 1 | 2,
  franchiseLimit: 0 | 150
): number | null {
  const offers = getRcLiabilityOffers(prices, petIdx)
  // The pricing API returns offers with has_previous_insurance=false regardless of Q1.
  // Only number_of_previous_claims drives the price difference.
  const match = offers.find(
    (o) =>
      o.number_of_previous_claims === numberOfPreviousClaims &&
      o.franchise_limit === franchiseLimit &&
      o.annual_limit === RC_ANNUAL_LIMIT &&
      o.has_rescue_option === false
  )
  return match?.price ?? null
}

export function getMinRcPrice(
  prices: PricingResponse | undefined,
  petIdx: number,
  franchiseLimit: 0 | 150
): number | null {
  const offers = getRcLiabilityOffers(prices, petIdx)
  // Filter to our plan parameters (breed-specific franchise, annual=10M, no rescue) for a meaningful "from" price
  const planOffers = offers.filter(
    (o) =>
      o.franchise_limit === franchiseLimit &&
      o.annual_limit === RC_ANNUAL_LIMIT &&
      !o.has_rescue_option
  )
  if (planOffers.length === 0) return null
  return Math.min(...planOffers.map((o) => o.price))
}
